/**
 * Two-pane shell for builder mode: chat on the left, the object being built on
 * the right, with the chat column giving up width rather than being covered.
 *
 * The panel is a real child, laid out in the flow. It used to be the global CRM
 * entity slideout — a `fixed right-0` portal floating above a reserved strip of
 * negative space — and this component's only job was to size that strip. Owning
 * the panel outright removes a whole class of problem: no portal stacking, no
 * "supply a placeholder ONLY when no live panel is mounted" caveat, and no
 * second floating copy of the record when the slideout also opens on it.
 *
 * The dynamic width is an inline style because the panel is user-resizable, so
 * the value cannot be a Tailwind class. It is isolated here — in one named
 * component — rather than repeated at each call site, per the design-system
 * rule on inline styles.
 */

import type { ReactNode } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { cx } from '@/utils/cx';

interface BuilderSplitProps {
  /** Width of the object panel in pixels. */
  panelWidth: number;
  /** Whether to reserve space at all. When false the chat uses full width. */
  isPanelVisible: boolean;
  /**
   * Whether the user is dragging the panel's resize handle.
   *
   * While true the chat column's width transition is suppressed so it tracks
   * the pointer 1:1. Left on, the transition would restart on every mousemove
   * and the column would visibly lag behind the drag.
   */
  isPanelResizing?: boolean;
  /** The chat column. */
  children: ReactNode;
  /** The object being built. Rendered in the flow, not floated above it. */
  panel?: ReactNode;
}

export function BuilderSplit({
  panelWidth,
  isPanelVisible,
  isPanelResizing = false,
  children,
  panel,
}: BuilderSplitProps) {
  // The panel is hidden below `lg` (too narrow for a 600px column beside a
  // chat), so the reservation has to disappear with it. Reserving width for a
  // `display: none` panel squeezes the chat to nothing beside an empty gap —
  // it reads as "the panel vanished and took the layout with it".
  // Gated on the SAME breakpoint as the panel's own `hidden lg:flex` in
  // ReasoningChat — keep these two in lockstep (see Background in the spec).
  const isLargeScreen = useBreakpoint('lg');
  const showPanel = isPanelVisible && isLargeScreen && Boolean(panel);
  // Below `lg`: chat is full screen; the panel (see showMobileLayout below)
  // opens over it on demand instead of being squeezed into a permanent dock.
  const showMobileLayout = isPanelVisible && !isLargeScreen && Boolean(panel);

  if (showMobileLayout) {
    // The CHAT is the full-screen primary surface on mobile; the record panel
    // is not stacked above it. It used to be, with the chat squeezed into a
    // 192px dock beneath — which left neither surface usable. The panel now
    // opens over the chat on demand, and on this pre-conversation route there
    // is nothing to review yet, so it does not open at all.
    return (
      <div className='flex flex-col h-full w-full overflow-hidden'>
        {children}
      </div>
    );
  }

  return (
    <div className='flex h-full w-full overflow-hidden'>
      <div
        className={cx(
          'flex flex-col min-w-0',
          !isPanelResizing && 'transition-[width] duration-300 ease-out'
        )}
        style={{
          width: showPanel ? `calc(100% - ${panelWidth}px)` : '100%',
        }}
      >
        {children}
      </div>
      {showPanel ? (
        <div
          className='flex shrink-0 h-full border-l border-[var(--color-border-secondary)] bg-bg-primary'
          style={{ width: `${panelWidth}px` }}
        >
          {panel}
        </div>
      ) : null}
    </div>
  );
}
